import React, { useMemo } from 'react';
import { Button } from 'antd';
import moment from 'moment';
import { IBestDataset } from '../../../../../../types/analytics';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { useExtremeValuesParams } from '../../../hooks/useExtremeValuesParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import ExtremeValuesInputForm from './ExtremeValuesInputForm';
import ExtremeValuesChart from './ExtremeValuesChart';

interface ExtremeValuesProps {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void;
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  extremeValuesImage: string;
  extremeValuesLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  isOfflineDataset: boolean;
}

export default function ExtremeValues(props: ExtremeValuesProps) {
  const {
    datasets,
    extremeValuesImage,
    extremeValuesLinks,
    error,
    updating,
    pointName,
    isOfflineDataset,
  } = props;

  const {
    variablesX,
    variableXIndex,
    startDate,
    endDate,
    categories,
    onUpdateExtremeValues,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  } = useExtremeValuesParams(props);

  // TODO: enable cache
  // const upsertExtremeValuesCache = useAnalyticsCacheStore(state => state.upsertExtremeValuesCache)

  // useEffect(() => {
  //   if (!props.previewDatasetId || !variableXIndex) return;

  //   const key = `${props.previewDatasetId}_${variableXIndex}_${props.coordinates[1]},${props.coordinates[0]}`;
  //   upsertExtremeValuesCache(key, {
  //     variableXIndex,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     extremeValuesImage,
  //     extremeValuesLinks
  //   })
  // }, [upsertExtremeValuesCache, props.previewDatasetId, props.coordinates, variableXIndex, startDate, endDate, props.isForAllAnalytics, extremeValuesImage, upsertExtremeValuesCache, extremeValuesLinks]);

  const plotInputContent = useMemo(
    () => (
      <ExtremeValuesInputForm
        datasets={datasets}
        dataset={props.previewDatasetId}
        onSelectDataset={onSelectDataset}
        categories={categories}
        variablesX={variablesX}
        variableXIndex={variableXIndex}
        onVariableChange={doVariableChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={updating}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      datasets,
      onSelectDataset,
      categories,
      variablesX,
      variableXIndex,
      doVariableChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      updating,
      error,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          id='Analytics-ExtremeValues-Update_Button'
          disabled={updating || isOfflineDataset}
          loading={updating}
          onClick={() => onUpdateExtremeValues()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [updating, onUpdateExtremeValues]
  );

  const chartContent = useMemo(
    () => (
      <ExtremeValuesChart
        extremeValuesImage={extremeValuesImage}
        extremeValuesLinks={extremeValuesLinks}
        pointName={pointName}
        isLoading={updating}
        error={error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [extremeValuesImage, extremeValuesLinks, pointName, updating, error]
  );

  const additionalContent = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          height: '100%',
        }}
      >
        <div style={{ flex: 1 }} />
      </div>
    ),
    []
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  );
}
